import React, { useEffect, useRef, useState } from "react";
import Footer from "../../core/common/footer/Footer";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import { all_routes } from "../router/all_routes";
import CancelAuthentication from "./components/CancelAuthentication";
import StepProgress from "./components/StepProgress";
import { useSelector } from "react-redux";
import useApi from "../../core/customhook/useApi";
import { apibaseurl } from "../../environment";
import SnipperLoader from "../../core/common/loader/SnipperLoader";

const SelfieVerification = () => {
  const navigate = useNavigate();

  const reduxDashboardData = useSelector(
    (state: any) => state?.dashboard?.dashboardData,
  );

  const candidateId = reduxDashboardData?.assignmentDetails?.candidate_id;

  const getAddressAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const updateSelfieAPI = useApi<any>(
    `/api/v1/candidates/addresses/${reduxDashboardData?.assignmentDetails?.candidate_id}/live-selfie`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const [selfie, setSelfie] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [confirmSelfie, setConfirmSelfie] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [detailData, setDetailData] = useState<any>(null);
  const [originalSelfieUrl, setOriginalSelfieUrl] = useState("");
  const [originalVerified, setOriginalVerified] = useState(false);

  useEffect(() => {
    if (!candidateId) return;
    const fetchDetails = async () => {
      try {
        const res = await getAddressAPI.callApi();

        if (res?.success) {
          const data = res.data[0];

          setDetailData(data);
          const serverImage = `${apibaseurl}${data.live_selfie_url}`;
          setPreview(serverImage);
          // setConfirmSelfie(data.live_selfie_verified);
          setOriginalSelfieUrl(serverImage ?? "");
          setOriginalVerified(data.live_selfie_verified ?? false);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchDetails();
  }, [candidateId]);

  // Open Camera
  const openCamera = async () => {
    try {
      setCameraError("");

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
        },
        audio: false,
      });

      // Store stream first
      setStream(mediaStream);

      // Render camera section
      setShowCamera(true);
    } catch (error) {
      console.error("Camera error:", error);

      setCameraError(
        "Unable to access camera. Please upload your selfie instead.",
      );

      setShowCamera(false);
    }
  };

  // Attach camera stream after video element is rendered
  useEffect(() => {
    if (!showCamera || !stream) return;

    const video = videoRef.current;

    if (!video) return;

    video.srcObject = stream;

    const playVideo = async () => {
      try {
        await video.play();
      } catch (error) {
        console.error("Unable to play camera stream:", error);
      }
    };

    playVideo();
  }, [showCamera, stream]);

  // Capture Selfie
  const captureSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        const file = new File([blob], "live-selfie.png", {
          type: "image/png",
        });

        setSelfie(file);
        setPreview(URL.createObjectURL(file));

        stopCamera();
      },
      "image/png",
      1,
    );
  };

  // Stop Camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setStream(null);
    setShowCamera(false);
  };

  // Upload Image
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Only JPG, JPEG, and PNG images are allowed.",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File Too Large",
        text: "Image size should be less than 10 MB.",
      });
      return;
    }

    setSelfie(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleNext = async () => {
    if (!preview) {
      Swal.fire({
        icon: "warning",
        title: "Selfie Required",
        text: "Please capture or upload your selfie.",
      });
      return;
    }

    if (!confirmSelfie) {
      Swal.fire({
        icon: "warning",
        title: "Declaration Required",
        text: "Please accept the declaration.",
      });
      return;
    }

    // const selfieChanged = selfie !== preview;
    const selfieChanged = selfie !== null;
    const declarationChanged = confirmSelfie !== originalVerified;

    // Nothing changed
    if (!selfieChanged && !declarationChanged) {
      navigate(all_routes.templateQuestion);
      return;
    }

    try {
      const formData = new FormData();

      if (selfie) {
        formData.append("selfie", selfie);
      }

      formData.append("address_type", detailData.address_type);

      const response = await updateSelfieAPI.callApi(formData);

      if (response?.success) {
        navigate(all_routes.templateQuestion);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {getAddressAPI.loading ? (
          <SnipperLoader />
        ) : (
          <>
            {/* Shared step tracker — this page is step 2 of 5, step 1 already done */}
            <StepProgress
              totalSteps={5}
              currentStep={3}
              completedSteps={[1, 2]}
            />

            <div className="card overflow-hidden">
              {/* Header */}

              <div
                className="card-header bg-white"
                style={{
                  padding: "28px",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex justify-content-center align-items-center me-3"
                    style={{
                      width: "58px",
                      height: "58px",
                      borderRadius: "14px",
                      background: "#FFF3EC",
                    }}
                  >
                    <i
                      className="ti ti-camera"
                      style={{
                        fontSize: "30px",
                        color: "#F26522",
                      }}
                    ></i>
                  </div>

                  <div>
                    <h4 className="fw-bold mb-1">Live Selfie Verification</h4>

                    <p
                      className="mb-0 text-muted"
                      style={{
                        fontSize: "14px",
                      }}
                    >
                      Please take a live selfie or upload your most recent
                      photograph.
                    </p>
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                {/* Instructions */}

                <div
                  className="border rounded-3 mb-4"
                  style={{
                    overflow: "hidden",
                  }}
                >
                  <div
                    role="button"
                    onClick={() => setShowInstructions(!showInstructions)}
                    className="d-flex justify-content-between align-items-center"
                    style={{
                      padding: "16px 20px",
                      background: "#FAFAFA",
                      cursor: "pointer",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="me-3"
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          background: "#FFF3EC",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <i
                          className="ti ti-info-circle"
                          style={{
                            color: "#F26522",
                            fontSize: "22px",
                          }}
                        ></i>
                      </div>

                      <div>
                        <h6 className="fw-semibold mb-0">Instructions</h6>

                        <small className="text-muted">
                          Please read before capturing your selfie.
                        </small>
                      </div>
                    </div>

                    <button
                      className="btn btn-sm"
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        background: "#FFF3EC",
                        color: "#F26522",
                        fontWeight: 700,
                        fontSize: "20px",
                        padding: 0,
                      }}
                    >
                      {showInstructions ? "−" : "+"}
                    </button>
                  </div>

                  {showInstructions && (
                    <div
                      style={{
                        padding: "20px",
                        background: "#fff",
                        borderTop: "1px solid #ececec",
                      }}
                    >
                      <ul
                        className="mb-0"
                        style={{
                          paddingLeft: "18px",
                        }}
                      >
                        <li className="mb-2 text-dark">
                          Capture a clear front-facing selfie.
                        </li>

                        <li className="mb-2 text-dark">
                          Maximum image size should not exceed 2 MB.
                        </li>

                        <li className="mb-2 text-dark">
                          Allowed formats: JPG, JPEG and PNG.
                        </li>

                        <li className="mb-2 text-dark">
                          PDF files are not accepted.
                        </li>

                        <li className="mb-2 text-dark">
                          Ensure your full face is clearly visible without mask,
                          sunglasses or cap.
                        </li>

                        <li className="mb-0 text-dark">
                          Selfie should be captured from the verified address.
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                {/* ====================== Image Preview ====================== */}

                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h5 className="fw-semibold mb-1">
                        <i
                          className="ti ti-photo me-2"
                          style={{ color: "#F26522" }}
                        ></i>
                        Selfie Preview
                      </h5>

                      <small className="text-muted">
                        Review your captured or uploaded selfie before
                        continuing.
                      </small>
                    </div>
                  </div>

                  <div
                    className="rounded-3 overflow-hidden"
                    style={{
                      border: "2px dashed #E3E6EA",
                      background: "#FAFBFC",
                      minHeight: "360px",
                    }}
                  >
                    {preview ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{
                          minHeight: "360px",
                        }}
                      >
                        <img
                          src={preview}
                          alt="Preview"
                          className="img-fluid"
                          style={{
                            maxHeight: "340px",
                            objectFit: "contain",
                            borderRadius: "10px",
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="d-flex flex-column justify-content-center align-items-center"
                        style={{
                          minHeight: "360px",
                        }}
                      >
                        <div
                          className="d-flex justify-content-center align-items-center mb-3"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            background: "#FFF3EC",
                          }}
                        >
                          <i
                            className="ti ti-camera"
                            style={{
                              fontSize: "40px",
                              color: "#F26522",
                            }}
                          ></i>
                        </div>

                        <h5 className="fw-semibold mb-2">No Selfie Uploaded</h5>

                        <p
                          className="text-muted mb-0"
                          style={{
                            maxWidth: "400px",
                            textAlign: "center",
                          }}
                        >
                          Capture a live selfie using your camera or upload a
                          recent photograph.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <hr className="my-4" />

                {/* ====================== Camera Error ====================== */}

                {cameraError && (
                  <div className="alert alert-danger d-flex align-items-center">
                    <i className="ti ti-alert-circle me-2 fs-5"></i>

                    {cameraError}
                  </div>
                )}

                {/* ====================== Upload Buttons ====================== */}

                <div className="row g-3 mb-4">
                  <div className="col-lg-3">
                    <button
                      className="btn btn-primary w-100"
                      style={{
                        height: "48px",
                      }}
                      onClick={openCamera}
                    >
                      <i className="ti ti-camera me-2"></i>
                      Open Camera
                    </button>
                  </div>

                  <div className="col-lg-3">
                    <input
                      type="file"
                      className="form-control"
                      accept=".jpg,.jpeg,.png"
                      onChange={handleImageUpload}
                      style={{
                        height: "48px",
                      }}
                    />
                  </div>
                </div>

                {/* ====================== Live Camera ====================== */}

                {showCamera && (
                  <div
                    className="rounded-3 p-4 mt-2"
                    style={{
                      border: "1px solid #E6E8EC",
                      background: "#FAFBFC",
                    }}
                  >
                    <div className="d-flex align-items-center mb-3">
                      <i
                        className="ti ti-video me-2"
                        style={{
                          color: "#F26522",
                          fontSize: "22px",
                        }}
                      ></i>

                      <h6 className="fw-semibold mb-0">Live Camera Preview</h6>
                    </div>

                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="rounded w-100"
                      style={{
                        maxHeight: "420px",
                        objectFit: "cover",
                        background: "#000",
                      }}
                    />

                    <div className="text-center mt-4">
                      <button
                        className="btn btn-success px-4"
                        onClick={captureSelfie}
                      >
                        <i className="ti ti-camera-check me-2"></i>
                        Capture Selfie
                      </button>
                    </div>
                  </div>
                )}

                <canvas
                  ref={canvasRef}
                  style={{
                    display: "none",
                  }}
                />

                <hr style={{ margin: "32px 0", borderColor: "#919297" }} />

                {/* ====================== Declaration ====================== */}

                <div
                  className="rounded-3 p-3"
                  style={{
                    background: "#FFF9F5",
                    border: "1px solid #FFE3D1",
                  }}
                >
                  <div className="d-flex align-items-start">
                    <div
                      className="d-flex justify-content-center align-items-center me-3"
                      style={{
                        width: "42px",
                        height: "42px",
                        borderRadius: "50%",
                        background: "#FFF3EC",
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className="ti ti-shield-check"
                        style={{
                          color: "#F26522",
                          fontSize: "22px",
                        }}
                      ></i>
                    </div>

                    <div className="w-100">
                      <h6 className="fw-semibold mb-2">Declaration</h6>

                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="confirmSelfie"
                          checked={confirmSelfie}
                          onChange={(e) => setConfirmSelfie(e.target.checked)}
                        />

                        <label
                          htmlFor="confirmSelfie"
                          className="form-check-label"
                          style={{
                            lineHeight: "1.7",
                          }}
                        >
                          I confirm that the photograph submitted is my live
                          selfie, taken by me at the verified address, and is
                          accurate to the best of my knowledge.
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ====================== Footer ====================== */}
                <hr style={{ margin: "32px 0", borderColor: "#919297" }} />

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-3 mt-3">
                  <small className="text-dark text-center text-md-start">
                    Ensure all details are correct before proceeding.
                  </small>

                  <div className="d-flex gap-2 justify-content-center justify-content-md-end">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="btn btn-outline-primary flex-fill flex-md-grow-0 px-4"
                    >
                      Back
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary flex-fill flex-md-grow-0 px-4"
                      onClick={handleNext}
                    >
                      Save & Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {showCancelModal && (
          <CancelAuthentication
            show={showCancelModal}
            onClose={() => setShowCancelModal(false)}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SelfieVerification;
